import { isRef, onUnmounted, ref, type Ref } from 'vue';

type EventBusHandler<T = any> = (e: CustomEvent<T>) => void;
type EventBusCallback<T> = (data?: T) => void;

export enum EventBusEvents {
  CrmDetailLoaded = 'event-bus:crm:detail-loaded',
}

export const useEventBus = () => {
  const eventRegistry = ref<Map<string, EventBusHandler>>(new Map());

  const emitEvent = <T>(name: EventBusEvents, data?: T, sendTo?: Ref<HTMLElement> | HTMLElement): void => {
    const e = data
      ? new CustomEvent(name, { detail: data })
      : new CustomEvent(name);

    if (sendTo) {
      if (isRef(sendTo)) {
        sendTo.value.dispatchEvent(e);
      } else {
        sendTo.dispatchEvent(e);
      }
    } else {
      window.dispatchEvent(e);
    }
  };

  const listen = <T>(name: EventBusEvents, onMessage: EventBusCallback<T>, attachTo?: Ref<HTMLElement> | HTMLElement): void => {
    const eventHandler: EventBusHandler<T> = e => {
      if (typeof onMessage === 'function') {
        if (e.detail !== undefined) {
          onMessage(e.detail);
        } else {
          onMessage();
        }
      }
    };

    if (attachTo) {
      if (isRef(attachTo)) {
        attachTo.value.addEventListener(name, eventHandler);
      } else {
        attachTo.addEventListener(name, eventHandler);
      }
    } else {
      window.addEventListener(name, eventHandler);
      eventRegistry.value.set(name, eventHandler);
    }
  };

  onUnmounted(() => {
    eventRegistry.value.forEach((handler, key) => {
      window.removeEventListener(key, handler);
    });
  });

  return {
    emitEvent,
    listen,
  };
};
